import * as React from 'react';
import { Body, ColumnFlexBox, RowFlexBox } from '../../components/templates/Wrappers'
import colors from '../../globals/colors';
import { lang } from '../../globals/Translations';
import { TranslatedTerms } from '../../globals/TranslatedTerms';
import { IsTablet, useWindowSize } from '../../globals/UIHooks'
import styled from 'styled-components'
import { SelectInput } from '../atoms/Inputs';
import Language, { AvailabeLanguages, LanguageId } from '../../shared/Language';
import { upadteInvestorLanguage } from '../../globals/ApiHooks';
import { NOTIFICATIONS_ALIAS, getLanguage, getLocalItem } from '../../globals/LocalStorageHooks';
import { signOut } from '../../globals/AuthHooks';

interface Props {
  loading?: boolean;
  onlyMobileMenu?: boolean;
  noBoxShadow?: boolean
}

const MenuSize = 300

const MenuItemTitle = styled.span`
  padding: 0px 0px 0px 24px;
  text-decoration: none;
  font-weight: 700;
`

const DrawerButtonMobile = styled.button`
  z-index: 102;
  position: fixed;
  right: 0;
  top: 0;
  margin: 20px;
  transition: 100ms ease;
  background-color: rgba(46, 143, 250, 0.2);
  color: ${colors.light};
  font-weight: 700;
  width: 140px;
  :active, :hover, :focus{
    color: ${colors.light};
    font-weight: 700;

  }
`
const ActionWeapper = styled.div`
  padding: 10px 6px 10px 16px;
  margin: 4px 14px;
`

const MenuItemComponent = styled.a`
  padding: 10px 6px 10px 16px;
  margin: 4px 14px;
  transition: 350ms ease;
  text-decoration: none;
  font-weight: 400;
  border-radius: 10px;
  :hover{
    background-color: ${colors.light};
    transition: 350ms ease;
    color: ${colors.primary};
  }
`

const PolicyLink = styled.a`
  color: ${colors.opacityText};
  text-decoration: none;
  font-size: 13px;
  line-height: 1.2em;
  padding: 4px;
  :hover{
    color: ${colors.lightest};
  }
`

const RedDotWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 2px;
  background-color: ${colors.danger};
  color: ${colors.lightest};
  font-size: 10px;
  line-height: 0px;
  width: 15px;
  height: 15px;
  border-radius: 15px;
`

const RedDot: React.FC<{ refrence: string }> = ({ refrence }) => {
  const [localCount, setLocalCount] = React.useState<string>()

  const getItem =  () => {
    const result = getLocalItem(NOTIFICATIONS_ALIAS)
    if(result) setLocalCount(result)
    else setLocalCount(undefined)
  }

  React.useEffect(()=> {
    getItem()
    window.addEventListener('storage', getItem)
  }, [refrence])

  if(localCount) return <RedDotWrapper>{localCount}</RedDotWrapper>
  return <></>
}


type MenuItem = { name: TranslatedTerms, url: string, countRefrence?: string }

const GeneralMenuItems: MenuItem[] = [
  { name: TranslatedTerms.HOME, url: '/dashboard' },
  { name: TranslatedTerms.PROJECTS, url: '/projects' },
  { name: TranslatedTerms.FEED, url: '/feed' },
  { name: TranslatedTerms.PROFILE, url: '/profile' },
  { name: TranslatedTerms.MESSAGES, url: '/notifications', countRefrence: "notifications" },
]

export const TERMS_LINK = "https://www.fundbricks.com/legal/terms"
export const COOKIES_LINK = "https://www.fundbricks.com/legal/cookies"
export const RISK_LINK = "https://www.fundbricks.com/legal/risk"
export const PRIVACY_LINK = "https://www.fundbricks.com/legal/privacy"

const ExternalMenuItems: MenuItem[] = [
  { name: TranslatedTerms.FAQ, url: 'https://www.fundbricks.com/faq' },
  { name: TranslatedTerms.CONTACT, url: 'https://www.fundbricks.com/contact' },
]
const PolicyLinks: MenuItem[] = [
  { name: TranslatedTerms.USER_POLICY, url: TERMS_LINK },
  { name: TranslatedTerms.COOKIE_POLICY, url: COOKIES_LINK },
  { name: TranslatedTerms.PRIVACY_POLICY, url: PRIVACY_LINK },
  { name: TranslatedTerms.RISK_POLICY, url: RISK_LINK }
]

const LogOutItem: MenuItem = { name: TranslatedTerms.LOG_OUT, url: '/logout' }

const MenuItemActive: React.CSSProperties = {
  backgroundColor: colors.primaryOpacity,
  backdropFilter: "blur(30px)"
}

export const Menu: React.FC<{ drawer: boolean, setLoading(state: boolean): void }> = ({ drawer, setLoading }) => {
  const [open, setOpen] = React.useState(drawer)
  const currentURL = window.location.pathname
  const mobileMenuClosed = drawer && !open

  const defaultValue = getLanguage()

  const setLanguage = async (language: LanguageId) => {
    setLoading(true)
    await upadteInvestorLanguage({ language })
    setLoading(false)
  }

  React.useEffect(() => { setOpen(false) }, [])

  return (
    <>
      {drawer && <DrawerButtonMobile style={{ backgroundColor: colors.dark }}
        onClick={() => setOpen(!open)}>{(open ? lang(TranslatedTerms.CLOSE_MENU) : lang(TranslatedTerms.MENU)).toUpperCase()}</DrawerButtonMobile>}

      <ColumnFlexBox style={{
        minWidth: MenuSize, padding: 0, height: '100%', zIndex: 101, left: mobileMenuClosed ? -MenuSize : 0, top: 0, bottom: 0,
        position: 'fixed', backgroundColor: colors.lighter, overflow: "auto", justifyContent: 'space-between',
        transition: "all 350ms ease", boxShadow: colors.defaultShadow
      }}>
        <ColumnFlexBox>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: 20 }}>
            <img
              width="250px"
              src="https://prod-attachments-bucket-fb-2-0.s3.eu-west-1.amazonaws.com/59c23268-89c9-4132-9a6c-c97a7aa61eff.png"
              alt="Fundbricks logo" />
          </div>
          <MenuItemTitle style={{ marginTop: 24 }}>{lang(TranslatedTerms.GENERAL).toUpperCase()}</MenuItemTitle>
          {GeneralMenuItems.map((item, key) =>
            <MenuItemComponent key={key} href={item.url} style={currentURL === item.url ? MenuItemActive : {}}>
              <RowFlexBox>
                {lang(item.name).toUpperCase()}
                {item.countRefrence && <RedDot refrence={item.countRefrence}/>}
              </RowFlexBox>
            </MenuItemComponent>)}
          <MenuItemTitle style={{ marginTop: 24 }}>{lang(TranslatedTerms.SUPPORT).toUpperCase()}</MenuItemTitle>
          {ExternalMenuItems.map((item, key) =>
            <MenuItemComponent key={key} href={item.url} target='_blank'>{lang(item.name).toUpperCase()}</MenuItemComponent>)}
        </ColumnFlexBox>

        <div>
          <ActionWeapper>
            <SelectInput
              options={AvailabeLanguages} fieldName={TranslatedTerms.LANGUAGE}
              itemRender={(e: Language) => e.localDisplayName} value={defaultValue}
              getValue={(e: Language) => e.languageId} onSelect={(id: LanguageId) => setLanguage(id)} />
          </ActionWeapper>
          <ColumnFlexBox style={{ alignItems: 'center', backgroundColor: colors.dark, color: colors.light, paddingBottom: 20 }}>
            {PolicyLinks.map((item, key) => <PolicyLink key={key} href={item.url} target='_blank'>{lang(item.name)}</PolicyLink>)}
            <PolicyLink onClick={signOut}>{lang(LogOutItem.name)}</PolicyLink>
          </ColumnFlexBox>
        </div>
      </ColumnFlexBox>
    </>
  );
};

export const BodyWithMenu: React.FC<Props> = ({ children, loading, onlyMobileMenu, noBoxShadow }) => {

  const [menuActionsLoading, setMenuActionsLoading] = React.useState(false)

  const sizes = useWindowSize()

  const drawer = onlyMobileMenu || IsTablet(sizes.width || 0)

  return (
    <Body style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }} loading={loading || menuActionsLoading}>
      <Menu drawer={drawer} setLoading={setMenuActionsLoading} />
      <div style={{
        boxShadow: noBoxShadow ? "" : "rgba(149, 157, 165, 0.2) 0px 8px 24px", width: '100%', padding: 20,
        marginLeft: drawer ? 0 : MenuSize, zIndex: 10, backgroundColor: colors.lightest, paddingBottom: 100
      }}>
        {children}
      </div>
    </Body>
  );
};